import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fetchPreviousResults } from "../lib/api";
import { useCachedData } from "../lib/useCachedData";

function formatDMY(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export function PreviousResultsTable({ variant, cityLabel = "Morning" }) {
  const [page, setPage] = useState(1);
  const limit = 15;
  const { data } = useCachedData(
    `mst:prev:${variant}:${page}`,
    () => fetchPreviousResults(variant, page, limit),
    {}
  );

  const items = data?.items || [];
  const pages = data?.pages || 1;

  return (
    <div data-testid="previous-results-table">
      <div className="overflow-x-auto rounded-xl border border-slate-200 card-shadow bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#0f172a] text-white">
              <th className="px-3 py-3 text-left font-semibold">Date</th>
              <th className="px-3 py-3 text-left font-semibold">City</th>
              <th className="px-3 py-3 text-center font-semibold">F/R</th>
              <th className="px-3 py-3 text-center font-semibold">S/R</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r, i) => (
              <tr
                key={r.date + i}
                data-testid={`prev-row-${i}`}
                className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}
              >
                <td className="px-3 py-3 whitespace-nowrap font-medium text-slate-800">
                  {formatDMY(r.date)}
                  <span className="block text-[11px] text-slate-400 font-normal">{r.day}</span>
                </td>
                <td className="px-3 py-3 text-slate-600">{r.city || cityLabel}</td>
                <td className="px-3 py-3 text-center">
                  <span className="font-num font-extrabold text-lg text-[#ff4500]">{r.fr}</span>
                </td>
                <td className="px-3 py-3 text-center">
                  <span className="font-num font-extrabold text-lg text-slate-900">{r.sr}</span>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-slate-400">
                  Loading results…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <button
          data-testid="prev-page-btn"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-slate-300 text-sm font-semibold disabled:opacity-40 hover:bg-slate-100"
        >
          <ChevronLeft size={16} /> Prev
        </button>
        <span className="text-sm text-slate-500" data-testid="page-indicator">
          Page {page} of {pages}
        </span>
        <button
          data-testid="next-page-btn"
          disabled={page >= pages}
          onClick={() => setPage((p) => Math.min(pages, p + 1))}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-slate-300 text-sm font-semibold disabled:opacity-40 hover:bg-slate-100"
        >
          Next <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
